import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Sparkles } from "lucide-react";
import { formatINR } from "@/lib/format";

const useDebounced = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

export const PricingSuggestion = ({ locality, listingType, bhk, areaSqft, onApply }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const bhkN = Number(bhk) || 0;
  const areaN = Number(areaSqft) || 0;
  const key = `${locality}|${listingType}|${bhkN}|${areaN}`;
  const debounced = useDebounced(key, 500);
  const lastKey = useRef(null);

  useEffect(() => {
    if (!locality || areaN < 100) {
      setData(null);
      return;
    }
    if (debounced === lastKey.current) return;
    lastKey.current = debounced;
    setLoading(true);
    api.get("/pricing/suggest", { params: { locality, listing_type: listingType, bhk: bhkN, area_sqft: areaN } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [debounced, locality, listingType, bhkN, areaN]);

  if (!locality || areaN < 100) return null;

  if (loading && !data) {
    return <div className="text-xs text-[var(--ink-muted)] italic mt-1">Fetching pricing comparables…</div>;
  }

  if (!data) return null;

  if (!data.available) {
    return (
      <div className="mt-2 p-3 rounded-xl bg-[var(--surface)] border border-[var(--line)] flex items-start gap-2" data-testid="pricing-unavailable">
        <Sparkles className="w-4 h-4 text-[var(--ink-muted)] mt-0.5" />
        <div className="text-xs text-[var(--ink-muted)]">{data.message}</div>
      </div>
    );
  }

  return (
    <div className="mt-2 p-4 rounded-xl bg-[rgba(200,90,50,0.05)] border border-[rgba(200,90,50,0.25)]" data-testid="pricing-suggestion">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-[var(--primary)] uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5" /> AI Pricing Suggestion
        </div>
        <span className="text-[10px] text-[var(--ink-muted)]">{data.sample_size} comparables · {data.confidence} confidence</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <div className="p-2 rounded-lg bg-white">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)]">Fair Low</div>
          <div className="font-mono text-sm font-bold text-[var(--teak)]">{formatINR(data.low, listingType)}</div>
        </div>
        <div className="p-2 rounded-lg bg-[var(--primary)] text-white">
          <div className="text-[10px] uppercase tracking-wider opacity-90">Suggested</div>
          <div className="font-mono text-sm font-bold">{formatINR(data.median, listingType)}</div>
        </div>
        <div className="p-2 rounded-lg bg-white">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)]">Fair High</div>
          <div className="font-mono text-sm font-bold text-[var(--teak)]">{formatINR(data.high, listingType)}</div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-[var(--ink-muted)]">
          Locality rate: ₹{data.rate_low.toLocaleString("en-IN")}–₹{data.rate_high.toLocaleString("en-IN")}/sq.ft
        </div>
        <button
          type="button"
          onClick={() => onApply?.(data.median)}
          className="text-xs font-semibold text-[var(--primary)] hover:underline"
          data-testid="apply-suggested-price"
        >
          Apply suggested price →
        </button>
      </div>
    </div>
  );
};
